import React from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { Col } from "react-bootstrap";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import useResponsiveImageUrl from "src/utils/useResponsiveImageUrl";
import {
  BackgroundWrapper,
  TableContainer,
  StyledTable,
  DrawTimesContainer,
  BootstrapTableContainer,
  BootstrapStyledTable,
} from "./TBCBStyledComponents";

/**
 * Reusable Prize Table Component for TBCB promotional pages
 * 
 * @param {Object} props
 * @param {string} props.prizeType - Type of prize table: "diamond", "gold", or "silver"
 * @param {Object} props.tableData - Prize table data from translations
 * @param {string} props.imagePath - Path to images: "2025" or "2026"
 * @param {number} props.gridColumn - Grid column position (for grid layout)
 * @param {string} props.layout - Layout type: "grid" or "bootstrap"
 * @returns {JSX.Element}
 */
const TBCBPrizeTable = ({
  prizeType,
  tableData,
  imagePath,
  gridColumn,
  layout = "grid",
}) => {
  const { i18n } = useTranslation();

  if (!tableData) return null;

  const bgImage = useResponsiveImageUrl(
    `promotions/poker/triple-barrel-cash-blast-${imagePath}/${prizeType}-box.png`
  );

  if (layout === "bootstrap") {
    // Bootstrap layout for 2025 page
    return (
      <Col
        key={prizeType}
        xs={12}
        md={6}
        lg={true}
        className="d-flex px-0"
        style={{ alignSelf: "stretch" }}
      >
        <BootstrapTableContainer>
          <BootstrapStyledTable
            responsive
            variant="dark"
            className={prizeType}
            bgImage={bgImage}
          >
            <thead>
              <tr>
                <th colSpan={tableData.columns.length} className={prizeType}>
                  <ResponsiveImage
                    src={`promotions/poker/triple-barrel-cash-blast-${imagePath}/${prizeType}-${i18n.language}.png`}
                    alt={tableData.title}
                  />
                </th>
              </tr>
              <tr>
                {tableData.columns.map((col) => (
                  <th key={col.key}>{col.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.rows.map((row, idx) => (
                <tr key={idx}>
                  <td>
                    <ReactMarkdown>{row.envelopes}</ReactMarkdown>
                  </td>
                  <td>{row.amount}</td>
                </tr>
              ))}
              {tableData.drawTimesText && (
                <tr className="draw-times">
                  <td colSpan={tableData.columns.length} className={prizeType}>
                    <ReactMarkdown>{tableData.drawTimesText}</ReactMarkdown>
                  </td>
                </tr>
              )}
            </tbody>
          </BootstrapStyledTable>
        </BootstrapTableContainer>
      </Col>
    );
  }

  // Grid layout for 2026 pages
  return (
    <React.Fragment>
      {/* Background wrapper spans both rows */}
      <BackgroundWrapper
        bgImage={bgImage}
        style={{ gridColumn: gridColumn, gridRow: "1 / 3" }}
      />

      {/* Table in row 1 */}
      <TableContainer style={{ gridColumn: gridColumn, gridRow: 1 }} bgImage={bgImage}>
        <StyledTable responsive variant="dark" className={prizeType}>
          <thead>
            <tr>
              <th colSpan={tableData.columns.length} className={prizeType}>
                <ResponsiveImage
                  src={`promotions/poker/triple-barrel-cash-blast-${imagePath}/${prizeType}-${i18n.language}.png`}
                  alt={tableData.title}
                />
              </th>
            </tr>
            <tr>
              {tableData.columns.map((col) => (
                <th key={col.key}>{col.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.rows.map((row, idx) => (
              <tr key={idx}>
                <td>
                  <ReactMarkdown>{row.envelopes}</ReactMarkdown>
                </td>
                <td>{row.amount}</td>
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </TableContainer>

      {/* Draw times in row 2 */}
      {tableData.drawTimesText && (
        <DrawTimesContainer
          className={prizeType}
          style={{ gridColumn: gridColumn, gridRow: 2 }}
        >
          <ReactMarkdown>{tableData.drawTimesText}</ReactMarkdown>
        </DrawTimesContainer>
      )}
    </React.Fragment>
  );
};

export default TBCBPrizeTable;
